import { defer, EMPTY, Observable, of } from "rxjs"
import { ofType, StateObservable } from "redux-observable"
import { catchError, map, switchMap } from "rxjs/operators"
import { stringify } from "querystring"

import type { Action, StateObject } from "../../../redux/types"
import type { EpicDependencies } from "../../../rxjs/configureEpics"
import { Config } from "../../../../utils/config"
import { getHttpHeaders, getSelectedAccountId } from "../../../auth/selectors"
import { EventType } from "../../../../events/types"
import { createEventRequest, requestEvent } from "../../../../events/utils"
import { logger } from "../../../../utils/loggerWrap"
import {
    GOOGLE_BUSINESS_AUTH_REQUESTED,
    GOOGLE_BUSINESS_INFO_FETCH_REQUESTED,
    GOOGLE_BUSINESS_INTEGRATION_REMOVE_REQUESTED,
    GOOGLE_BUSINESS_LOCATION_SELECTED,
    googleBusinessInfoFetchFailed,
    googleBusinessInfoFetchSucceeded,
    googleBusinessIntegrationFailed,
    googleBusinessIntegrationRemoveFailed,
    googleBusinessIntegrationRemoveSucceeded,
    googleBusinessIntegrationSucceeded,
} from "./actions"
import { fetchGoogleBusinessInfo } from "./requests"
import { locationDetailAgendaMounted } from "../actions"

export const googleBusinessAuthRequestedEpic = (
    action$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    action$.pipe(
        ofType(GOOGLE_BUSINESS_AUTH_REQUESTED),
        switchMap((action: Action) => {
            const { locationId, accountId } = action.payload

            const params = stringify({
                locationId: locationId,
                accountId: accountId,
            })

            window.location.href = `${Config.webApiBaseUrl}/google-business/oauth?${params}`
            return EMPTY
        })
    )

export const googleBusinessInfoFetchRequestedEpic = (
    action$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    action$.pipe(
        ofType(GOOGLE_BUSINESS_INFO_FETCH_REQUESTED),
        switchMap((action: Action) => {
            const { locationId } = action.payload
            const headers = getHttpHeaders(deps.getState())

            return defer(() => fetchGoogleBusinessInfo(headers, locationId)).pipe(
                map(data => googleBusinessInfoFetchSucceeded(data)),
                catchError(error => {
                    logger.error("Failed to fetch Google Business info", { error: error })
                    return of(googleBusinessInfoFetchFailed(error.message || "Failed to fetch Google Business info"))
                })
            )
        })
    )

export const googleBusinessLocationSelectedEpic = (
    action$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    action$.pipe(
        ofType(GOOGLE_BUSINESS_LOCATION_SELECTED),
        switchMap((action: Action) => {
            const { figureLocationId, figureAccountId, googleAccountResourceName, googleLocation } = action.payload
            const headers = getHttpHeaders(deps.getState())

            return defer(() =>
                requestEvent(
                    headers,
                    createEventRequest(
                        EventType.ExternalServiceIntegrationCreateRequested,
                        {
                            accountId: figureAccountId,
                            locationId: figureLocationId,
                            correlationId: deps.idgen.next(),
                        },
                        {
                            type: "google-business-profile",
                            locationId: figureLocationId,
                            accountId: figureAccountId,
                            googleAccountResourceName: googleAccountResourceName,
                            googleLocationResourceName: googleLocation.resourceName,
                            googleLocationName: googleLocation.title,
                        }
                    )
                )
            ).pipe(
                switchMap(() => {
                    logger.info("Google Business Profile integration created successfully", {
                        figureLocationId: figureLocationId,
                        googleLocationResourceName: googleLocation.resourceName,
                    })
                    return [
                        googleBusinessIntegrationSucceeded(figureLocationId, googleLocation.resourceName),
                        locationDetailAgendaMounted(),
                    ]
                }),
                catchError(error => {
                    logger.error("Failed to create Google Business Profile integration", { error: error })
                    return of(googleBusinessIntegrationFailed(error.message || "Failed to create integration"))
                })
            )
        })
    )

export const googleBusinessIntegrationRemoveRequestedEpic = (
    action$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    action$.pipe(
        ofType(GOOGLE_BUSINESS_INTEGRATION_REMOVE_REQUESTED),
        switchMap((action: Action) => {
            const { figureLocationId } = action.payload
            const headers = getHttpHeaders(deps.getState())
            const accountId = getSelectedAccountId(deps.getState())

            if (!accountId) {
                logger.error("Cannot remove Google Business Profile integration: accountId not found in state")
                return of(googleBusinessIntegrationRemoveFailed("Account ID not found"))
            }

            return defer(() =>
                requestEvent(
                    headers,
                    createEventRequest(
                        EventType.ExternalServiceIntegrationRemoved,
                        {
                            accountId: accountId,
                            locationId: figureLocationId,
                            correlationId: deps.idgen.next(),
                        },
                        {
                            type: "google-business-profile",
                            locationId: figureLocationId,
                        }
                    )
                )
            ).pipe(
                switchMap(() => {
                    logger.info("Google Business Profile integration removed successfully", {
                        figureLocationId: figureLocationId,
                    })
                    return [googleBusinessIntegrationRemoveSucceeded(figureLocationId), locationDetailAgendaMounted()]
                }),
                catchError(error => {
                    logger.error("Failed to remove Google Business Profile integration", {
                        error: error,
                        figureLocationId: figureLocationId,
                    })
                    return of(googleBusinessIntegrationRemoveFailed(error.message || "Failed to remove integration"))
                })
            )
        })
    )

export default [
    googleBusinessAuthRequestedEpic,
    googleBusinessInfoFetchRequestedEpic,
    googleBusinessLocationSelectedEpic,
    googleBusinessIntegrationRemoveRequestedEpic,
]
